import { useCallback, useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Modal from '../ui/Modal';
import { Skeleton } from '../Skeleton';
import {
    listInsights,
    createInsight,
    type Insight,
    type InsightType,
    type Priority,
} from '../../mentorshipService';
const TYPES: InsightType[] = ['behavioral', 'technical', 'risk'];
const PRIORITIES: Priority[] = ['low', 'medium', 'high'];

function NewInsightModal({
    onClose,
    onCreated,
}: {
    onClose: () => void;
    onCreated: (i: Insight) => void;
}) {
    const [userId, setUserId] = useState('');
    const [type, setType] = useState<InsightType>('behavioral');
    const [priority, setPriority] = useState<Priority>('medium');
    const [trigger, setTrigger] = useState('');
    const [prompt, setPrompt] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const submit = async () => {
        if (!prompt.trim() || !userId.trim()) return;
        setSaving(true);
        setError(null);
        try {
            const i = await createInsight({
                user_id: Number(userId),
                type,
                priority,
                trigger_context: trigger.trim(),
                prompt: prompt.trim(),
            });
            onCreated(i);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal open onClose={onClose} size="md" ariaLabel="New insight">
            <Modal.Header onClose={onClose}>New insight</Modal.Header>
            <Modal.Body>
                <label htmlFor="ic-user">Mentee user id</label>
                <input
                    id="ic-user"
                    type="number"
                    value={userId}
                    onChange={(e) => setUserId(e.target.value)}
                />
                <label htmlFor="ic-type">Type</label>
                <select
                    id="ic-type"
                    value={type}
                    onChange={(e) => setType(e.target.value as InsightType)}
                >
                    {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                <label htmlFor="ic-priority">Priority</label>
                <select
                    id="ic-priority"
                    value={priority}
                    onChange={(e) => setPriority(e.target.value as Priority)}
                >
                    {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
                <label htmlFor="ic-trigger">Trigger context</label>
                <input
                    id="ic-trigger"
                    value={trigger}
                    onChange={(e) => setTrigger(e.target.value)}
                />
                <label htmlFor="ic-prompt">Prompt (If ___ → Then ___)</label>
                <textarea
                    id="ic-prompt"
                    rows={3}
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                />
                {error && <p className="report-submit__error">{error}</p>}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="ghost" size="md" onClick={onClose}>Cancel</Button>
                <Button variant="primary" size="md" onClick={submit} disabled={saving}>
                    {saving ? 'Saving…' : 'Save'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

function MentorInsightsCurator() {
    const [insights, setInsights] = useState<Insight[] | null>(null);
    const [loading, setLoading] = useState(true);
    const [showNew, setShowNew] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const page = await listInsights();
            setInsights(page.results);
        } catch {
            setInsights([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        void load();
    }, [load]);

    return (
        <div>
            <div className="mentor-projects__header">
                <h3>Curated insights</h3>
                <Button
                    variant="primary"
                    size="md"
                    icon={<Plus size={16} />}
                    onClick={() => setShowNew(true)}
                >
                    New insight
                </Button>
            </div>
            {loading ? (
                <Skeleton width="100%" height="80px" borderRadius="12px" />
            ) : !insights || insights.length === 0 ? (
                <div className="empty-state">
                    <h3>No insights yet</h3>
                    <p>Manual insights help mentees apply your guidance.</p>
                </div>
            ) : (
                <ul>
                    {insights.map((i) => (
                        <li key={i.id}>
                            <Card variant="row">
                                <Card.Header>
                                    <div>
                                        <p>{i.prompt}</p>
                                        <span className="mentorship-chip mentorship-chip--module">
                                            {i.type} · {i.priority}
                                        </span>
                                    </div>
                                </Card.Header>
                            </Card>
                        </li>
                    ))}
                </ul>
            )}
            {showNew && (
                <NewInsightModal
                    onClose={() => setShowNew(false)}
                    onCreated={(i) => {
                        setShowNew(false);
                        setInsights((prev) => [i, ...(prev ?? [])]);
                    }}
                />
            )}
        </div>
    );
}

export default MentorInsightsCurator;
